import { useCallback, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

import {
  namespaces,
  useSharedTranslation,
} from 'us-react-components/dist/hooks/useSharedTranslation';
import { localDateTimeInputToApi } from 'us-react-components/dist/utils/date';
import {
  axios,
  Booking,
  BookingService,
  Parent,
  ParentService,
} from 'us-web-services';

import { Button, ButtonType } from '../../components';
import { useDependencies } from '../../dependencies';

const MAX_MESSAGE = 2500;
const MAX_RATE_CREDIT = 35;
const DEFAULT_MESSAGE =
  "I've added this booking at your request. It will make it easy for us both to track it and get support 7 days a week.";

type Step = 'detail' | 'date';

interface Form {
  parent?: Parent & { acceptsCredit?: boolean };
  numberOfChildren: number;
  rate: string;
  paymentType: 'cash' | 'credit';
  message: string;
  start: string;
  end: string;
}

export const SitterBookingCreate = observer((): JSX.Element => {
  const { t } = useSharedTranslation(namespaces.caregiver);
  const navigate = useNavigate();
  const { stores } = useDependencies();
  const user = stores.user.user;
  const sitter = stores.sitter.sitter;

  const acceptsCredit = !!(sitter as unknown as { acceptsCreditCards?: boolean })?.acceptsCreditCards;

  const [step, setStep] = useState<Step>('detail');
  const [parentQuery, setParentQuery] = useState<string>('');
  const [parentResults, setParentResults] = useState<Parent[]>([]);
  const [searching, setSearching] = useState<boolean>(false);
  const [lastRate, setLastRate] = useState<number | null>(null);
  const [form, setForm] = useState<Form>({
    parent: undefined,
    numberOfChildren: 1,
    rate: '',
    paymentType: 'cash',
    message: DEFAULT_MESSAGE,
    start: '',
    end: '',
  });
  const [maxCreditRate, setMaxCreditRate] = useState<number>(MAX_RATE_CREDIT);
  const [maxCreditRateLoading, setMaxCreditRateLoading] = useState<boolean>(false);
  const [validation, setValidation] = useState({ parent: true, rate: true, credit: true });
  const [submitting, setSubmitting] = useState(false);
  const [dateError, setDateError] = useState<string>('');

  const setField = <K extends keyof Form>(key: K, value: Form[K]): void => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  useEffect(() => {
    document.title = 'UrbanSitter - Add a booking';
  }, []);

  // Debounced parent search.
  useEffect(() => {
    if (!user?.id || !parentQuery.trim() || form.parent) {
      setParentResults([]);
      return undefined;
    }
    setSearching(true);
    const handle = setTimeout(() => {
      void (async (): Promise<void> => {
        try {
          const resp = await ParentService.getByFilter({
            params: {
              sitterId: user.id,
              contacted: true,
              name: parentQuery.trim(),
              userId: user.id,
              sort: 'profile.firstName',
              limit: 10,
            },
          });
          setParentResults(((resp.data?.data ?? []) as Parent[]).slice(0, 10));
        } catch {
          setParentResults([]);
        } finally {
          setSearching(false);
        }
      })();
    }, 250);
    return (): void => clearTimeout(handle);
  }, [parentQuery, user?.id, form.parent]);

  const sitterId = user?.id;
  const selectedParentId = form.parent?.id;

  useEffect(() => {
    if (!sitterId || !selectedParentId) {
      setMaxCreditRate(MAX_RATE_CREDIT);
      setMaxCreditRateLoading(false);
      return undefined;
    }

    let ignore = false;
    setMaxCreditRateLoading(true);

    void (async (): Promise<void> => {
      try {
        const resp = await BookingService.getByFilter({
          params: {
            sitterId,
            parentId: selectedParentId,
            userId: sitterId,
            type: 'job',
            statuses: ['closed_completed'],
            paymentType: 'credit',
            sort: 'rate',
            'rate.dir': 'desc',
            limit: 1,
          },
        });
        if (ignore) return;
        const highestCompletedCreditRate = ((resp.data?.data ?? []) as Booking[])[0]?.rate ?? 0;
        setMaxCreditRate(Math.max(MAX_RATE_CREDIT, highestCompletedCreditRate));
      } catch {
        if (!ignore) setMaxCreditRate(MAX_RATE_CREDIT);
      } finally {
        if (!ignore) setMaxCreditRateLoading(false);
      }
    })();

    return (): void => {
      ignore = true;
    };
  }, [sitterId, selectedParentId]);

  const selectParent = (parent: Parent): void => {
    setField('parent', parent as Form['parent']);
    setParentQuery(parent.name ?? `${parent.firstName ?? ''} ${parent.lastName ?? ''}`);
    setParentResults([]);

    if (user?.id && parent.id) {
      void (async (): Promise<void> => {
        try {
          const resp = await BookingService.getByFilter({
            params: { sitterId: user.id, userId: user.id, parentId: parent.id },
          });
          const bookings = (resp.data?.data ?? []) as Booking[];
          if (bookings.length) {
            const last = bookings[0];
            if (last.rate) {
              setLastRate(last.rate);
              setField('rate', String(last.rate));
            }
            if (last.numberOfChildren) {
              setField('numberOfChildren', last.numberOfChildren);
            }
          } else {
            setLastRate(null);
            setField('numberOfChildren', (parent as unknown as { kids?: unknown[] }).kids?.length || 1);
          }
        } catch {
          setLastRate(null);
        }
      })();
    }
  };

  const cancelBooking = useCallback((): void => {
    navigate('/caregiver/dashboard');
  }, [navigate]);

  const getPaymentLabel = (): string => {
    if (acceptsCredit && lastRate) {
      return t('pages.sitterBookingCreate.lastRatePaid', { rate: lastRate });
    }
    if (acceptsCredit) {
      return t('pages.sitterBookingCreate.payBy');
    }
    return t('pages.sitterBookingCreate.noCredit');
  };

  const remainingMessage = MAX_MESSAGE - form.message.length;

  const validateDetail = (): boolean => {
    const rate = Number(form.rate);
    const nextValidation = {
      parent: !!form.parent?.id,
      rate: !!form.rate && Number.isFinite(rate) && rate >= 8,
      credit:
        form.paymentType === 'cash' ||
        !Number.isFinite(rate) ||
        rate <= maxCreditRate,
    };
    setValidation(nextValidation);

    return nextValidation.parent && nextValidation.rate && nextValidation.credit;
  };

  const next = (): void => {
    if (!validateDetail()) return;
    setStep('date');
  };

  const addJob = async (): Promise<void> => {
    if (!form.parent?.id || !user?.id || submitting) return;
    if (!form.start || !form.end) {
      setDateError(t('pages.sitterBookingCreate.bothTimesRequired'));
      return;
    }
    const startMs = new Date(form.start).getTime();
    const endMs = new Date(form.end).getTime();
    if (Number.isNaN(startMs) || Number.isNaN(endMs) || endMs <= startMs) {
      setDateError(t('pages.sitterBookingCreate.endAfterStart'));
      return;
    }
    setDateError('');
    setSubmitting(true);
    try {
      await BookingService.create({
        parentId: form.parent.id,
        sitterId: user.id,
        numberOfChildren: form.numberOfChildren,
        rate: Number(form.rate),
        paymentType: form.paymentType,
        message: form.message,
        start: localDateTimeInputToApi(form.start),
        end: localDateTimeInputToApi(form.end),
      } as never);
      toast.success(t('pages.sitterBookingCreate.heading'));
      navigate('/bookings');
    } catch (e) {
      if (axios.isAxiosError(e) && e.response?.data?.errors?.[0]?.message) {
        toast.error(e.response.data.errors[0].message);
      } else {
        toast.error(t('pages.sitterBookingCreate.addBookingError'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const parent = form.parent as (Parent & { pictureUrl?: string; address?: { street?: string; city?: string; state?: string; zip?: string } }) | undefined;

  if (step === 'detail') {
    return (
      <div className='container py-6 max-w-3xl mx-auto'>
        <div className='us-card rounded p-5'>
          <h1 className='text-2xl font-semibold mb-2'>{t('pages.sitterBookingCreate.heading')}</h1>
          <div className='bg-sapphire-50 p-3 rounded mb-4 text-sm'>
            {t('pages.sitterBookingCreate.onlyForAgreed')}
          </div>

          <div className='grid grid-cols-1 md:grid-cols-3 gap-4 mb-4'>
            <div className={validation.parent ? '' : 'has-error'}>
              <label className='block font-semibold mb-1'>
                {t('pages.sitterBookingCreate.parentNameLabel')}
              </label>
              <input
                className='form-control w-full border border-neutral-300 rounded px-3 py-2'
                value={parentQuery}
                onChange={(e): void => {
                  setParentQuery(e.target.value);
                  if (form.parent && e.target.value !== (form.parent.name ?? '')) {
                    setField('parent', undefined);
                    setLastRate(null);
                  }
                }}
                placeholder={t('pages.sitterBookingCreate.parentSearchPlaceholder')}
              />
              {parentResults.length > 0 && (
                <div className='border border-neutral-300 rounded mt-1 bg-white max-h-48 overflow-auto z-10 relative'>
                  {parentResults.map((p) => (
                    <Button
                      key={p.id}
                      buttonType={ButtonType.tertiary}
                      className='block w-full text-left px-3 py-2 hover:bg-neutral-100'
                      onClick={(): void => selectParent(p)}
                    >
                      <div className='font-medium'>
                        {p.firstName} {p.lastName}
                      </div>
                      {p.address?.city && (
                        <div className='text-sm text-neutral-600'>
                          {p.address.city}, {p.address.state}
                        </div>
                      )}
                    </Button>
                  ))}
                </div>
              )}
              {searching && (
                <div className='text-sm text-neutral-500 mt-1'>
                  {t('pages.sitterBookingCreate.searching')}
                </div>
              )}
              {!validation.parent && (
                <div className='text-red-600 text-sm mt-1'>
                  {t('pages.sitterBookingCreate.selectParent')}
                </div>
              )}
            </div>

            <div>
              <label htmlFor='num_children' className='block font-semibold mb-1'>
                {t('pages.sitterBookingCreate.numChildrenLabel')}
              </label>
              <select
                id='num_children'
                className='form-control w-full border border-neutral-300 rounded px-3 py-2'
                value={form.numberOfChildren}
                onChange={(e): void => setField('numberOfChildren', Number(e.target.value))}
              >
                {[1, 2, 3, 4, 5, 6].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </div>

            <div className={validation.rate && validation.credit ? '' : 'has-error'}>
              <label htmlFor='rate' className='block font-semibold mb-1'>
                {t('pages.sitterBookingCreate.ratePerHour')}
              </label>
              <div className='flex items-center border border-neutral-300 rounded overflow-hidden'>
                <span className='px-3 bg-neutral-100'>$</span>
                <input
                  id='rate'
                  className='form-control flex-1 px-2 py-2'
                  type='number'
                  min={8}
                  value={form.rate}
                  onChange={(e): void => setField('rate', e.target.value.replace(/[^0-9.]/g, ''))}
                />
              </div>
              {!validation.rate && (
                <div className='text-red-600 text-sm mt-1'>
                  {t('pages.sitterBookingCreate.minRate')}
                </div>
              )}
              {!validation.credit && (
                <div className='text-red-600 text-sm mt-1'>
                  {t('pages.sitterBookingCreate.creditRateCap', {
                    maxRate: maxCreditRate,
                  })}
                </div>
              )}
            </div>
          </div>

          <div className='mb-4'>
            <label htmlFor='paymentType' className='block font-semibold mb-1'>
              {getPaymentLabel()}
            </label>
            <select
              id='paymentType'
              className='form-control w-full border border-neutral-300 rounded px-3 py-2'
              disabled={!acceptsCredit}
              value={form.paymentType}
              onChange={(e): void =>
                setField('paymentType', e.target.value as 'cash' | 'credit')
              }
            >
              <option value='cash'>{t('pages.sitterBookingCreate.payCash')}</option>
              {acceptsCredit && (
                <option value='credit'>{t('pages.sitterBookingCreate.useFamilyCredit')}</option>
              )}
            </select>
          </div>

          {parent?.address && (
            <div className='mb-4'>
              <label className='font-semibold mr-1'>
                {t('pages.sitterBookingCreate.locationLabel')}
              </label>
              <span>
                {parent.address.street}, {parent.address.city},{' '}
                {parent.address.state} {parent.address.zip}
              </span>
            </div>
          )}

          <div className='mb-4'>
            <div className='flex justify-between items-center mb-1'>
              <label htmlFor='message' className='font-semibold'>
                {t('pages.sitterBookingCreate.messageLabel')}
              </label>
              <i className='text-neutral-500 text-sm'>
                {t('pages.sitterBookingCreate.charactersRemaining', {
                  count: remainingMessage,
                })}
              </i>
            </div>
            <textarea
              id='message'
              className='form-control w-full border border-neutral-300 rounded p-2'
              rows={4}
              maxLength={MAX_MESSAGE}
              value={form.message}
              onChange={(e): void => setField('message', e.target.value)}
            />
          </div>

          <div className='flex gap-3'>
            <Button
              buttonType={ButtonType.primary}
              onClick={next}
              disabled={maxCreditRateLoading}
            >
              {t('pages.sitterBookingCreate.continueCta')}
            </Button>
            <Button buttonType={ButtonType.secondary} onClick={cancelBooking}>
              {t('common.cancel')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // date step
  return (
    <div className='container py-6 max-w-3xl mx-auto'>
      <div className='us-card rounded p-5'>
        <h2 className='text-xl font-semibold mb-3'>
          {t('pages.sitterBookingCreate.addDateHeading')}
        </h2>
        <div className='flex items-start gap-3 mb-4'>
          {parent?.pictureUrl && (
            <img
              src={parent.pictureUrl}
              alt={parent.firstName}
              className='w-16 h-16 rounded-full object-cover'
            />
          )}
          <div>
            <h3 className='font-semibold mb-0'>
              {parent?.firstName} {parent?.lastName}
            </h3>
            <div>
              {t('pages.sitterBookingCreate.childCount', { count: form.numberOfChildren })}
            </div>
            <div>${form.rate} per hour</div>
            <Button
              buttonType={ButtonType.tertiary}
              onClick={(): void => setStep('detail')}
              className='underline cursor-pointer text-sm bg-transparent border-0 p-0'
            >
              {t('pages.sitterBookingCreate.change')}
            </Button>
          </div>
        </div>

        <div className='grid grid-cols-1 md:grid-cols-2 gap-3 mb-3'>
          <div>
            <label htmlFor='start' className='block font-semibold mb-1'>
              {t('pages.sitterBookingCreate.start')}
            </label>
            <input
              id='start'
              type='datetime-local'
              className='form-control w-full border border-neutral-300 rounded px-3 py-2'
              value={form.start}
              onChange={(e): void => setField('start', e.target.value)}
            />
          </div>
          <div>
            <label htmlFor='end' className='block font-semibold mb-1'>
              {t('pages.sitterBookingCreate.end')}
            </label>
            <input
              id='end'
              type='datetime-local'
              className='form-control w-full border border-neutral-300 rounded px-3 py-2'
              value={form.end}
              onChange={(e): void => setField('end', e.target.value)}
            />
          </div>
        </div>
        {dateError && <p className='text-red-600 text-sm mb-3'>{dateError}</p>}

        <div className='flex gap-3'>
          <Button buttonType={ButtonType.primary} onClick={addJob} disabled={submitting}>
            {t('pages.sitterBookingCreate.addJob')}
          </Button>
          <Button buttonType={ButtonType.secondary} onClick={cancelBooking}>
            {t('pages.sitterBookingCreate.cancel')}
          </Button>
        </div>
      </div>
    </div>
  );
});
